import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import type { AppRole } from '../shared/auth/account-api';
import { useAccountStatus } from '../shared/hooks/useAccountStatus';
import { PageLoader } from '../shared/ui/PageLoader';

import { useAuth } from './AuthProvider';

export const ROLE_HOME: Readonly<Record<AppRole, string>> = {
  superadmin: '/admin/institutes',
  institute: '/institute/dashboard',
  student: '/student/home',
};

interface RequireRoleProps {
  // Named `allow` (not `role`) to avoid the jsx-a11y/aria-role rule firing
  // on our app-role union; ARIA roles are a different namespace.
  allow: AppRole;
  children: ReactNode;
}

/**
 * Role check for a route. Always rendered INSIDE <AccountGate>, which has
 * already resolved (and cached) the same account status — so this costs no
 * extra round-trip and, crucially, shows no second loading card. Before
 * Phase 126 this fetched the role itself with no cache at all, so it flashed
 * "Loading…" on literally every refresh.
 *
 * The no-role and error branches are gone too: the gate above owns them, and
 * it renders NoAccessScreen, which (unlike the old card) actually offers a way
 * to sign out.
 */
export function RequireRole({ allow, children }: RequireRoleProps): JSX.Element {
  const { user } = useAuth();
  const { status, loading } = useAccountStatus(user?.id ?? null);

  if (loading || status === null) {
    return <PageLoader />;
  }

  if (status.role === null) {
    // Unreachable through the router (AccountGate handles it), but a role can
    // be revoked mid-session; send the user back through the gate rather than
    // rendering a screen with no exit.
    return <Navigate to="/app" replace />;
  }

  if (status.role !== allow) {
    // eslint-disable-next-line security/detect-object-injection -- status.role is a typed AppRole union, not user input
    return <Navigate to={ROLE_HOME[status.role]} replace />;
  }

  return <>{children}</>;
}
